import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Image} from 'react-native';
import {
  Text,
  Card,
  Chip,
  Appbar,
  ActivityIndicator,
  Snackbar,
  Dialog,
  Portal,
  Button,
  TextInput,
  IconButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {optionTypeApi} from '../../services/api/optionTypeApi';
import {colors, spacing} from '../../theme';

const RENDER_TYPE_LABELS = {
  TOGGLE: 'toggle',
  SINGLE_SELECT: 'singleSelect',
  MULTI_SELECT: 'multiSelect',
  TEXT: 'text',
};

const OptionTypeDetailScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {optionType: initialOptionType} = route.params;

  const [optionType, setOptionType] = useState(initialOptionType);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // Edit dialog state
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await optionTypeApi.listConsolidatedOptionTypes(null, null, true);
      const found = response.data.data.find(ot => ot.id === initialOptionType.id);
      if (found) {
        setOptionType(found);
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [initialOptionType.id, t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleToggleOption = useCallback(async (option, newValue) => {
    try {
      const response = await optionTypeApi.toggleOption(optionType.id, option.id, {isEnabled: newValue});
      setOptionType(response.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [optionType.id, t]);

  const openEditDialog = useCallback((option) => {
    setEditingOption(option);
    setDisplayName(option.name);
    setSortOrder(String(option.displayOrder));
    setDialogVisible(true);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (!editingOption) return;
    try {
      const response = await optionTypeApi.updateOptionOverride(optionType.id, editingOption.id, {
        displayNameOverride: displayName.trim() || null,
        sortOrderOverride: sortOrder ? parseInt(sortOrder, 10) : null,
      });
      setOptionType(response.data.data);
      setDialogVisible(false);
      setEditingOption(null);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [editingOption, optionType.id, displayName, sortOrder, t]);

  const sortByEnabled = (a, b) => (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0);
  const options = (optionType?.options || []).slice().sort(sortByEnabled);

  if (isLoading && !optionType) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={optionType?.name || ''} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
        <View style={styles.metaRow}>
          {optionType.hasOverride && (
            <Chip style={styles.overrideChip}>
              <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{t('optionType.customized')}</Text>
            </Chip>
          )}
          {optionType.renderType && (
            <Chip style={styles.renderChip}>
              <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>
                {t(`optionType.${RENDER_TYPE_LABELS[optionType.renderType]}`) || optionType.renderType}
              </Text>
            </Chip>
          )}
          {optionType.gender && (
            <Chip style={styles.genderChip}>
              <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>
                {optionType.gender === 'BOTH'
                  ? t('measurement.both')
                  : optionType.gender === 'MALE'
                  ? t('customer.male')
                  : t('customer.female')}
              </Text>
            </Chip>
          )}
        </View>

        <Text variant="titleMedium" style={styles.sectionTitle}>
          {t('optionType.options')} ({options.length})
        </Text>

        {options.length === 0 ? (
          <Text style={styles.emptyText}>{t('optionType.noOptions')}</Text>
        ) : (
          options.map(option => (
            <Card
              key={option.id}
              style={[styles.optionCard, !option.enabled && styles.optionDisabled]}>
              <Card.Content style={styles.optionRow}>
                <IconButton
                  icon={option.enabled ? 'check-circle' : 'circle-outline'}
                  size={24}
                  iconColor={option.enabled ? colors.primary : colors.textSecondary}
                  onPress={() => handleToggleOption(option, !option.enabled)}
                  style={styles.toggleBtn}
                />
                {option.imageUrl1 ? (
                  <Image source={{uri: option.imageUrl1}} style={styles.optionThumb} />
                ) : (
                  <View style={styles.optionLetterBox}>
                    <Text style={styles.optionLetter}>
                      {(option.name || '?').charAt(0).toUpperCase()}
                    </Text>
                  </View>
                )}
                <View style={styles.optionTextContainer}>
                  <Text
                    variant="bodyMedium"
                    style={[styles.optionName, !option.enabled && styles.textDisabled]}
                    numberOfLines={2}>
                    {option.name}
                  </Text>
                </View>
                <IconButton
                  icon="pencil-outline"
                  size={20}
                  iconColor={colors.textSecondary}
                  onPress={() => openEditDialog(option)}
                  style={styles.actionBtn}
                />
              </Card.Content>
            </Card>
          ))
        )}
        <View style={{height: 40}} />
      </ScrollView>

      {/* Edit Option Override Dialog */}
      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>{t('optionType.editOption')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('optionType.displayName')}
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <TextInput
              label={t('optionType.sortOrder')}
              value={sortOrder}
              onChangeText={setSortOrder}
              mode="outlined"
              keyboardType="numeric"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSaveOverride}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  metaRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, padding: spacing.md, paddingBottom: 0},
  overrideChip: {height: 28, paddingVertical: 0, backgroundColor: '#FFF3E0'},
  renderChip: {height: 28, paddingVertical: 0, backgroundColor: colors.secondary + '15'},
  genderChip: {height: 28, paddingVertical: 0, backgroundColor: colors.primary + '15'},
  sectionTitle: {fontWeight: '600', color: colors.text, paddingHorizontal: spacing.md, marginTop: spacing.md, marginBottom: spacing.sm},
  emptyText: {textAlign: 'center', color: colors.textSecondary, marginTop: spacing.xl, paddingHorizontal: spacing.md},
  optionCard: {
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  optionDisabled: {
    opacity: 0.5,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  toggleBtn: {margin: 0},
  optionThumb: {
    width: 44,
    height: 44,
    borderRadius: 6,
    backgroundColor: colors.border,
    marginRight: spacing.sm,
  },
  optionLetterBox: {
    width: 44,
    height: 44,
    borderRadius: 6,
    backgroundColor: '#F0EBF8',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  optionLetter: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.primary,
  },
  optionTextContainer: {flex: 1},
  optionName: {color: colors.text, fontWeight: '500'},
  textDisabled: {color: colors.textSecondary},
  actionBtn: {margin: 0},
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default OptionTypeDetailScreen;
